import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { eventQueries } from "../features/events/api/queries";
import { EventRow } from "../features/events/components/EventRow";
import { areaQueries } from "../features/weather/api/queries";
import type { Area } from "../features/weather/types";
import { DayTabs } from "../features/week/components/DayTabs";
import { groupByDay, totalEvents } from "../features/week/grouping";
import {
  datesIn,
  isoWeekNumber,
  isValidDate,
  shiftWeeks,
  todayIn,
  weekOf,
  type Week,
} from "../features/week/week";
import { AppHeader } from "../shared/ui/AppHeader";

/**
 * URL shape: `/week/cph?week=2026-07-27&day=2026-07-30`.
 *
 * The **week** is in the URL because it is the thing worth linking to and
 * reloading onto. The selected **day** is there too so a link can point at a
 * specific evening, but it is optional and defaults to the whole week.
 *
 * `week` is any date within the wanted week; it is normalised to that week's
 * Monday, so a hand-typed mid-week date works rather than erroring.
 */
interface WeekSearch {
  week?: string;
  day?: string;
}

/**
 * `validateSearch` is the trust boundary for URL input.
 *
 * An invalid date is dropped rather than rejected: `?week=lol` should open the
 * current week, not an error page. Rejecting would turn a stale bookmark into a
 * dead end for no benefit — there is nothing dangerous here to guard against,
 * only nonsense to ignore.
 */
function validateSearch(search: Record<string, unknown>): WeekSearch {
  const week = asDate(search.week);
  const day = asDate(search.day);

  /*
   * A `day` outside the requested week is dropped. Keeping it would leave the
   * strip with nothing selected while the list showed one day's events, which
   * looks like a rendering bug rather than a bad URL.
   *
   * With no `week` param the week is derived from the area's timezone, which is
   * not available here — so the check is deferred to the component, which has it.
   */
  const dayFitsWeek = day && week ? inWeek(day, weekOf(week)) : true;

  return { week, day: dayFitsWeek ? day : undefined };
}

function asDate(raw: unknown): string | undefined {
  return typeof raw === "string" && isValidDate(raw) ? raw : undefined;
}

function inWeek(date: string, week: Week): boolean {
  return date >= week.from && date <= week.to;
}

export const Route = createFileRoute("/_authenticated/week/$areaSlug")({
  validateSearch,
  /*
   * Declared **before** `loader`, not after: the loader's `deps` type is inferred
   * from this, and with the order reversed `deps` widens to `{}` and every field
   * access on it is an error.
   */
  loaderDeps: ({ search }) => ({ week: search.week }),
  /*
   * The week depends on the area's timezone when `?week=` is absent — "this week"
   * means this week *there*. So the areas list is loaded first and the week
   * derived from it, rather than defaulting to the viewer's today.
   */
  loader: async ({ context: { queryClient }, params, deps }) => {
    const { areas } = await queryClient.ensureQueryData(areaQueries.list());
    const area = areas.find((candidate) => candidate.slug === params.areaSlug);

    // An unknown slug renders the not-found state; nothing else to load.
    if (!area) return { area: null };

    const week = deps.week ? weekOf(deps.week) : weekOf(todayIn(area.timezone));

    await Promise.all([
      queryClient.ensureQueryData(weekEventsQuery(area.slug, week)),
      queryClient.ensureQueryData(
        areaQueries.outlooks(area.slug, week.from, week.to),
      ),
    ]);

    return { area };
  },
  component: WeekPage,
  errorComponent: ({ error }) => (
    <main className="p-6">
      <p role="alert" className="text-brand">
        Couldn’t load the week: {error.message}
      </p>
    </main>
  ),
});

/**
 * The week's events for one area.
 *
 * A function rather than a shared constant: the query key derives from this
 * object, so building it fresh keeps the key stable by value rather than by
 * reference.
 *
 * `limit` is the server's maximum. A week in one area will not approach it, and
 * paginating a week view would be worse than truncating one — but see the note in
 * the component about saying so if it ever happens.
 */
const weekEventsQuery = (areaSlug: string, week: Week) =>
  eventQueries.list({
    filter: {
      areaSlug,
      fromLocalDate: week.from,
      toLocalDate: week.to,
      /*
       * Opted out of the upcoming-only default, because this view has already
       * answered the question that default exists to answer: the window *is* the
       * week the user navigated to. Leaving it on would empty out every past week
       * the arrows reach, and half-empty the current one from Monday onward —
       * which reads as missing data rather than as a filter.
       */
      upcomingOnly: false,
    },
    limit: 100,
  });

/**
 * Splits on "does this area exist" **before** any hooks run.
 *
 * The unknown-area branch cannot sit inside the main component: an early return
 * ahead of `useSuspenseQuery` changes the number of hooks between renders, which
 * is a rules-of-hooks violation rather than a style preference. Two components
 * means each one calls its hooks unconditionally.
 */
function WeekPage() {
  const { area } = Route.useLoaderData();
  const { areaSlug } = Route.useParams();

  if (!area) return <UnknownArea slug={areaSlug} />;
  return <AreaWeek area={area} />;
}

function AreaWeek({ area }: { area: Area }) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const week = search.week
    ? weekOf(search.week)
    : weekOf(todayIn(area.timezone));

  const { data } = useSuspenseQuery(weekEventsQuery(area.slug, week));
  const { data: outlookData } = useSuspenseQuery(
    areaQueries.outlooks(area.slug, week.from, week.to),
  );

  const days = groupByDay(
    week,
    data.events,
    outlookData.areaOutlooks,
    area.timezone,
  );
  const total = totalEvents(days);

  const today = todayIn(area.timezone);
  const selectedDay =
    search.day && inWeek(search.day, week) ? search.day : null;
  const shown = selectedDay
    ? days.filter((day) => day.localDate === selectedDay)
    : days;

  /**
   * Navigates to another week.
   *
   * The day selection is **cleared** rather than carried across: "Thursday" in one
   * week is a different date in the next, and silently moving the selection to a
   * day the user did not pick is more surprising than landing on the whole week.
   */
  const goToWeek = (offset: number) => {
    const next = shiftWeeks(week.from, offset);
    void navigate({
      search: { week: next.from, day: undefined },
      params: { areaSlug: area.slug },
    });
  };

  return (
    <>
      <AppHeader />
      <main className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            {/* Eyebrow: the week and its dates, which the headline then doesn't
                have to carry. Same order as the mockup — identifier first. */}
            <p className="text-xs font-semibold tracking-widest text-brand uppercase">
              Week {isoWeekNumber(week.from)} · {formatRange(week)}
            </p>
            {/* The week number lives in the eyebrow above, so the headline is
                the plain phrase rather than repeating it. */}
            <h1 className="mt-2 text-4xl font-bold tracking-tight">
              {area.name} this week
            </h1>
            <p className="mt-2 text-sm text-content-muted">
              {total === 1 ? "1 event" : `${String(total)} events`}
              {/* Days are the area's, not the reader's — said once, here. */}
              {" · days shown in local time"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                goToWeek(-1);
              }}
              aria-label="Previous week"
              className="rounded-md border border-border-subtle px-3 py-1.5 hover:bg-surface-muted"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() =>
                void navigate({
                  search: { week: undefined, day: undefined },
                  params: { areaSlug: area.slug },
                })
              }
              className="rounded-md border border-border-subtle px-3 py-1.5 text-sm hover:bg-surface-muted"
            >
              This week
            </button>
            <button
              type="button"
              onClick={() => {
                goToWeek(1);
              }}
              aria-label="Next week"
              className="rounded-md border border-border-subtle px-3 py-1.5 hover:bg-surface-muted"
            >
              →
            </button>
          </div>
        </div>

        <div className="mt-6">
          <DayTabs
            days={days}
            selected={selectedDay}
            today={today}
            onSelect={(day) =>
              void navigate({
                search: { week: search.week, day: day ?? undefined },
                params: { areaSlug: area.slug },
                // Replace: picking through days should not fill the history.
                replace: true,
              })
            }
          />
        </div>

        {total === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-border-subtle p-10 text-center">
            <p className="font-medium">Nothing on in {area.name} this week</p>
            <p className="mt-1 text-sm text-content-muted">
              Try the next week, or browse everything.
            </p>
            <Link
              to="/events"
              className="mt-4 inline-block rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
            >
              All events
            </Link>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-8">
            {shown.map((day) => (
              <DaySection key={day.localDate} day={day} today={today} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}

/** One day's heading and rows. Empty days are skipped in the list, not in the strip. */
function DaySection({
  day,
  today,
}: {
  day: ReturnType<typeof groupByDay>[number];
  today: string;
}) {
  if (day.events.length === 0) return null;

  const date = new Date(`${day.localDate}T00:00:00Z`);

  return (
    <section>
      {/* An uppercase tracked heading over a hairline, as in the mockup. Not a
          filled band: the page body is itself `surface-muted`, so a muted fill
          would have nothing to sit against. */}
      <div className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-2">
        <h2 className="text-xs font-semibold tracking-widest uppercase">
          {dayHeadingFormat.format(date)}
          {day.localDate === today && (
            /* The leading space is inside the span deliberately: JSX drops
               whitespace between elements and lines, so without it the accessible
               name reads "Thursday 30 JulyToday". */
            <span className="ml-2 font-medium text-brand"> Today</span>
          )}
        </h2>
        <span className="text-xs text-content-muted">
          {day.events.length === 1
            ? "1 event"
            : `${String(day.events.length)} events`}
        </span>
      </div>
      {/*
        Still gapped cards, not flush rows. `EventRow` is shared with /events and
        /events/mine, so making it seamless here would restyle both — a bigger
        change than this one, and not one the week view needs.
      */}
      <ul className="mt-3 flex flex-col gap-3">
        {day.events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </ul>
    </section>
  );
}

function UnknownArea({ slug }: { slug: string }) {
  return (
    <>
      <AppHeader />
      <main className="p-6">
        <h1 className="text-2xl font-semibold">No such area</h1>
        <p className="mt-1 text-sm text-content-muted">
          There’s no area called “{slug}”. Areas are seeded, not user-created.
        </p>
        <Link
          to="/events"
          className="mt-4 inline-block rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
        >
          All events
        </Link>
      </main>
    </>
  );
}

/*
 * UTC formatting throughout: the inputs are bare `YYYY-MM-DD` parsed as UTC
 * midnight, so formatting in the viewer's zone would shift every label a day for
 * anyone west of UTC.
 */
const dayHeadingFormat = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  timeZone: "UTC",
});

const rangeFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

function formatRange(week: Week): string {
  const dates = datesIn(week);
  const first = new Date(`${dates[0]}T00:00:00Z`);
  const last = new Date(`${dates[dates.length - 1]}T00:00:00Z`);
  return `${rangeFormat.format(first)} – ${rangeFormat.format(last)}`;
}
